/**
 * ReportService — data access for standard HR reports.
 * Reuses biometric_logs attendance (BiometricDTR), leave_cred, leave_request, overtime, ob_*, manual_dtr.
 */
import BiometricDTR, {
  prefetchOrgLookups,
  prefetchEmployeeLookups,
} from "../../../components/DTR/BiometricDTR";
import { MONTHS } from "./reportConfig";
import { buildHierarchyWhere, HIERARCHY_FIELDS } from "../../../utils/hierarchy";

const CTO_TYPE = "Compensatory Time-Off";
/** One employee at a time — API rate limits are tight (Reports only). */
const ATTENDANCE_EMPLOYEE_CONCURRENCY = 1;
/** One lookup call at a time inside BiometricDTR (Reports only). */
const ATTENDANCE_LOOKUP_CONCURRENCY = 1;
/** Pause between employees to let the API recover (Reports only). */
const ATTENDANCE_EMPLOYEE_DELAY_MS = 700;
/**
 * Soft cap for attendance reports only (BiometricDTR is expensive).
 * Leave Credits / light queries page through the full org match.
 */
const ATTENDANCE_EMPLOYEE_LIMIT = 50;
const RATE_LIMIT_MESSAGE =
  "Too many requests to the server (rate limited). Narrow the Employee or Office filter and try again in a minute.";

const EMP_KEYS = [
  "id",
  "email",
  "biometricId",
  "agency",
  "Firstname",
  "Middlename",
  "surname",
  "region",
  "office",
  "division",
  "section",
  "unit",
  "cluster",
  "twentyFourSeven",
  "employmentType.name",
];

function isRateLimitError(error) {
  if (error == null) return false;
  const status =
    error.status ?? error.statusCode ?? error.code ?? error?.response?.status;
  if (status === 429 || String(status) === "429") return true;
  const raw =
    typeof error === "string"
      ? error
      : error.message || error.error || error.name || "";
  const msg = String(raw).toLowerCase();
  return (
    msg.includes("429") ||
    msg.includes("too many requests") ||
    msg.includes("rate limit") ||
    // CORS-masked 429 (preflight) often surfaces as connection/CORS failure after retries
    msg.includes("unable to connect") ||
    msg.includes("access-control-allow-origin") ||
    msg.includes("failed to fetch") ||
    msg.includes("network error")
  );
}

function throwIfRateLimited(error) {
  if (isRateLimitError(error)) {
    const err = new Error(RATE_LIMIT_MESSAGE);
    err.cause = error;
    err.isRateLimit = true;
    throw err;
  }
}

/** Max rows for a single Reports find (avoid pagination loops). */
const ONE_SHOT_LIMIT = 5000;

/**
 * Reports-only: one find with `where` (no large `$in`, no batch loops).
 * When many ids are needed, query with baseWhere only and filter client-side.
 */
async function findOnceByWhere(
  findUseCase,
  {
    collection,
    idField,
    ids,
    baseWhere = {},
    keys,
    include,
    sort,
    limit = ONE_SHOT_LIMIT,
  },
) {
  const idList = [...new Set((ids || []).filter(Boolean))];
  if (idList.length === 0) return [];

  const where =
    idList.length === 1
      ? { ...baseWhere, [idField]: idList[0] }
      : { ...baseWhere };

  const rows = await findUseCase.execute(collection, {
    where,
    ...(keys ? { keys } : {}),
    ...(include ? { include } : {}),
    ...(sort ? { sort } : {}),
    limit,
  });

  if (idList.length <= 1) return rows || [];

  const idSet = new Set(idList);
  const getId = (row) => {
    if (!row || !idField) return null;
    if (!idField.includes(".")) return row[idField];
    return idField.split(".").reduce((acc, key) => acc?.[key], row);
  };
  return (rows || []).filter((row) => idSet.has(getId(row)));
}

async function mapPool(items, concurrency, mapper, shouldAbort) {
  const list = items || [];
  if (list.length === 0) return [];
  const limit = Math.max(1, Math.min(concurrency || 1, list.length));
  const results = new Array(list.length);
  let next = 0;
  let aborted = null;

  const worker = async () => {
    while (next < list.length) {
      if (aborted) return;
      if (shouldAbort?.()) {
        aborted = new Error(RATE_LIMIT_MESSAGE);
        aborted.isRateLimit = true;
        aborted.isAborted = true;
        throw aborted;
      }
      const i = next++;
      try {
        results[i] = await mapper(list[i], i);
        if (
          ATTENDANCE_EMPLOYEE_DELAY_MS > 0 &&
          next < list.length &&
          !aborted &&
          !shouldAbort?.()
        ) {
          await new Promise((r) => setTimeout(r, ATTENDANCE_EMPLOYEE_DELAY_MS));
        }
      } catch (e) {
        aborted = e;
        throw e;
      }
    }
  };

  try {
    await Promise.all(Array.from({ length: limit }, () => worker()));
  } catch (e) {
    throw aborted || e;
  }
  return results;
}

function empName(emp) {
  if (!emp) return "";
  return `${emp.Firstname || ""} ${emp.Middlename || ""} ${emp.surname || ""}`
    .replace(/\s+/g, " ")
    .trim();
}

function officeLabel(emp) {
  if (!emp) return "";
  return (
    emp.officeName ||
    emp.divisionName ||
    emp.office ||
    emp.division ||
    emp.section ||
    emp.unit ||
    ""
  );
}

function statsList(log) {
  const s = log?.timeRecStats || log?.printTimeRecStats || [];
  return Array.isArray(s) ? s : s ? [s] : [];
}

function hasStat(log, tag) {
  return statsList(log).some(
    (t) => String(t).toUpperCase() === String(tag).toUpperCase(),
  );
}

function formatStats(log) {
  return statsList(log).join(", ");
}

function maxConsecutiveDayStreak(dateKeys) {
  const sorted = [...new Set((dateKeys || []).filter(Boolean))].sort();
  if (sorted.length === 0) return 0;
  let max = 1;
  let current = 1;
  for (let i = 1; i < sorted.length; i++) {
    const prev = new Date(`${sorted[i - 1]}T12:00:00.000Z`);
    const next = new Date(`${sorted[i]}T12:00:00.000Z`);
    const diffDays = Math.round((next - prev) / 86400000);
    if (diffDays === 1) {
      current += 1;
      if (current > max) max = current;
    } else {
      current = 1;
    }
  }
  return max;
}

function hasPresentPunch(log) {
  return (
    (log.timeIn && log.timeIn !== "--:--") ||
    (log.timePmIn && log.timePmIn !== "--:--") ||
    (log.timeOut && log.timeOut !== "--:--")
  );
}

function pickDate(value) {
  if (!value) return null;
  if (typeof value === "string" || typeof value === "number") {
    return String(value).slice(0, 10);
  }
  if (typeof value === "object") {
    const raw = value.$gte || value.$lte || value.start || value.end;
    return raw ? String(raw).slice(0, 10) : null;
  }
  return null;
}

function dateRangeFromFilters(filters) {
  const dr = filters.dateRange || {};
  const start =
    pickDate(dr.start) ||
    pickDate(dr.$gte) ||
    pickDate(dr.createdAt) ||
    pickDate(dr.dateFrom);
  const end =
    pickDate(dr.end) ||
    pickDate(dr.$lte) ||
    pickDate(dr.updatedAt) ||
    pickDate(dr.dateTo);
  return { start, end };
}

function monthYearFromFilters(filters) {
  const monthName = filters.month;
  const year = Number(filters.year);
  const monthIndex =
    typeof monthName === "number"
      ? monthName
      : MONTHS.indexOf(monthName);
  return { monthIndex, year, monthName };
}

function flattenDateRanges(selectedDate) {
  if (!Array.isArray(selectedDate)) return [];
  const dates = [];
  selectedDate.forEach((range) => {
    if (!Array.isArray(range) || range.length < 2) return;
    const [a, b] = range;
    if (a && b) {
      dates.push({ from: String(a).slice(0, 10), to: String(b).slice(0, 10) });
    }
  });
  return dates;
}

/** Overtime stores duration as "X hours and Y minutes" (see RequestOvertime). */
function overtimeHours(ot) {
  if (ot?.startTime && ot?.endTime) {
    const ms = new Date(ot.endTime) - new Date(ot.startTime);
    if (!Number.isNaN(ms) && ms > 0) return ms / 3600000;
  }
  const d = ot?.duration;
  if (typeof d === "number" && !Number.isNaN(d)) return d;
  if (typeof d === "string") {
    const asNum = Number(d);
    if (!Number.isNaN(asNum)) return asNum;
    const m = d.match(
      /(\d+)\s*hours?\s*(?:and\s*)?(\d+)?\s*minutes?/i,
    );
    if (m) return Number(m[1]) + (Number(m[2]) || 0) / 60;
  }
  return 0;
}

/** Normalize employee filter shapes from Pointer / multi-select / option UI. */
export function getSelectedEmployee(filters = {}) {
  const raw = filters.employee ?? filters.employees ?? filters.emp;
  if (!raw) return null;
  if (typeof raw === "string" || typeof raw === "number") {
    return { id: String(raw) };
  }
  if (Array.isArray(raw)) {
    for (const item of raw) {
      const emp = getSelectedEmployee({ employee: item });
      if (emp?.id) return emp;
    }
    return null;
  }
  if (typeof raw !== "object") return null;
  if (raw.id) return raw;
  if (raw.object?.id) return raw.object;
  if (
    raw.value &&
    (typeof raw.value === "string" || typeof raw.value === "number")
  ) {
    return raw.object?.id ? raw.object : { id: String(raw.value) };
  }
  return null;
}

export function hasHierarchyScope(hierarchy) {
  if (!hierarchy || typeof hierarchy !== "object") return false;
  return Object.values(hierarchy).some((v) => {
    if (v == null || v === "") return false;
    if (typeof v === "object") return Boolean(v.id);
    return true;
  });
}

/** Drop empty hierarchy levels so scope checks stay accurate. */
export function normalizeHierarchy(hierarchy) {
  if (!hierarchy || typeof hierarchy !== "object") return null;
  const cleaned = {};
  Object.entries(hierarchy).forEach(([key, value]) => {
    if (value == null || value === "") return;
    if (typeof value === "object") {
      if (value.id) cleaned[key] = value.id;
      return;
    }
    cleaned[key] = value;
  });
  return Object.keys(cleaned).length > 0 ? cleaned : null;
}

/** Services that must not scan the full employee table without a scope filter. */
export const EMPLOYEE_SCOPED_SERVICES = new Set([
  "fetchCocReport",
  "fetchCustomTimekeepingReport",
  "fetchDailyInOutReport",
  "fetchDtrCorrectionsReport",
  "fetchAwolReport",
  "fetchPerfectAttendanceReport",
  "fetchTardinessFrequencyReport",
  "fetchLeaveCreditsReport",
]);

export default class ReportService {
  constructor(findObjectUseCase) {
    this.find = findObjectUseCase;
    this._cancelled = false;
  }

  /** Reports-only: cancel in-flight attendance fan-out without touching Timesheet. */
  beginRun() {
    this._cancelled = false;
  }

  cancelRun() {
    this._cancelled = true;
  }

  isCancelled() {
    return this._cancelled;
  }

  assertEmployeeScope(filters = {}, { required = false } = {}) {
    const employee = getSelectedEmployee(filters);
    const hierarchy = normalizeHierarchy(filters.hierarchy) || {};
    const hasHierarchy = hasHierarchyScope(hierarchy);
    if (required && !employee?.id && !hasHierarchy) {
      throw new Error(
        "Select an Employee or Office/Organization filter before generating this report.",
      );
    }
    return { employee, hasHierarchy, hierarchy };
  }

  async resolveEmployees(filters = {}) {
    const { employee, hierarchy } = this.assertEmployeeScope(filters, {
      required: true,
    });

    // Specific employee wins over org filter.
    if (employee?.id) {
      const rows = await this.find.execute("employees", {
        where: { id: employee.id, "acl.read": { $exists: true } },
        include: ["employmentType"],
        keys: EMP_KEYS,
        limit: 1,
      });
      return rows || [];
    }

    // Match Employees / Leave Credits / Schedule Report: deepest selected org level only.
    // Employee docs store singular fields: region, office, division, section, unit, cluster.
    const hierarchyWhere = buildHierarchyWhere(hierarchy, "", HIERARCHY_FIELDS);
    if (!Object.keys(hierarchyWhere).length) {
      throw new Error(
        "Select an Employee or Office/Organization filter before generating this report.",
      );
    }

    const where = {
      "acl.read": { $exists: true },
      ...hierarchyWhere,
    };

    // Attendance reports may pass employeeLimit to avoid BiometricDTR stampede.
    // Leave Credits and other light reports use a single high-limit find.
    const maxEmployees =
      filters.employeeLimit != null
        ? Number(filters.employeeLimit)
        : ONE_SHOT_LIMIT;

    try {
      const rows = await this.find.execute("employees", {
        where,
        include: ["employmentType"],
        keys: EMP_KEYS,
        limit: maxEmployees,
        sort: { surname: 1, Firstname: 1 },
      });
      return rows || [];
    } catch (e) {
      throwIfRateLimited(e);
      throw e;
    }
  }

  /** Attendance reports: same org filter, but capped to avoid BiometricDTR rate limits. */
  resolveEmployeesForAttendance(filters = {}) {
    const employee = getSelectedEmployee(filters);
    return this.resolveEmployees({
      ...filters,
      employeeLimit: employee?.id ? 1 : ATTENDANCE_EMPLOYEE_LIMIT,
    });
  }

  async fetchAttendanceForEmployees(employees, monthIndex, year) {
    if (!employees?.length) return [];

    const start = new Date(Date.UTC(year, monthIndex, 1)).toISOString();
    const end = new Date(
      Date.UTC(year, monthIndex + 1, 0, 23, 59, 59, 999),
    ).toISOString();

    let sharedLookups = {};
    try {
      const org = await prefetchOrgLookups(employees, start, end);
      const empScoped = await prefetchEmployeeLookups(employees, start, end);
      sharedLookups = { ...org, ...empScoped };
    } catch (e) {
      throwIfRateLimited(e);
      console.warn(
        "Shared attendance lookup prefetch failed; falling back per employee",
        e,
      );
      sharedLookups = {};
    }

    const dtrOptions = {
      sharedLookups,
      lookupConcurrency: ATTENDANCE_LOOKUP_CONCURRENCY,
      serial: true,
    };

    const perEmployeeRows = await mapPool(
      employees,
      ATTENDANCE_EMPLOYEE_CONCURRENCY,
      async (employee) => {
        try {
          const logs = await BiometricDTR(
            monthIndex,
            year,
            employee,
            dtrOptions,
          );
          return (logs || []).map((log) => ({
            ...log,
            employee,
            employeeName: empName(employee),
            employeeNo: employee.agency || employee.biometricId || "",
            office: officeLabel(employee),
            scheduleLabel:
              log.scheduleUpdate?.selectedOption ||
              employee.selectedSchedule?.selectedOption ||
              "",
            timeRecStats: formatStats(log),
          }));
        } catch (e) {
          throwIfRateLimited(e);
          console.error("BiometricDTR failed for", employee?.id, e);
          return [];
        }
      },
      () => this.isCancelled(),
    );

    return perEmployeeRows.flat();
  }

  // ── 1. COC Availment ─────────────────────────────────────────────────────
  async fetchCocReport(filters = {}) {
    const { start, end } = dateRangeFromFilters(filters);
    // COC can run office-wide; still require a scope to avoid full-tenant scans
    const employees = await this.resolveEmployees(filters);
    const empIds = employees.map((e) => e.id).filter(Boolean);
    if (empIds.length === 0) return [];

    let credits;
    let leaveRequests;
    let overtime;
    try {
      credits = await findOnceByWhere(this.find, {
        collection: "leave_cred",
        idField: "empID",
        ids: empIds,
        baseWhere: { type: CTO_TYPE },
        keys: ["empID", "email", "type", "current"],
      });
      leaveRequests = await findOnceByWhere(this.find, {
        collection: "leave_request",
        idField: "employee.id",
        ids: empIds,
        baseWhere: {
          selectedType: { $regex: "Compensatory", $options: "i" },
          ...(start && end
            ? {
                dateRange: {
                  $elemMatch: {
                    $elemMatch: { 0: { $lte: end }, 1: { $gte: start } },
                  },
                },
              }
            : {}),
        },
        include: ["employee"],
        keys: [
          "employee",
          "selectedType",
          "creditDeduction",
          "dateRange",
          "stats",
        ],
      });
      overtime = await findOnceByWhere(this.find, {
        collection: "overtime",
        idField: "employee.id",
        ids: empIds,
        baseWhere: {
          stats: "Approved",
          ...(start && end
            ? {
                startTime: { $lte: `${end}T23:59:59.999Z` },
                endTime: { $gte: start },
              }
            : {}),
        },
        keys: ["employee", "duration", "startTime", "endTime", "stats"],
      });
    } catch (e) {
      throwIfRateLimited(e);
      throw e;
    }

    const creditByEmp = new Map(
      (credits || []).map((c) => [c.empID, Number(c.current) || 0]),
    );
    const availedByEmp = new Map();
    (leaveRequests || []).forEach((req) => {
      const id = req.employee?.id;
      if (!id) return;
      const list = availedByEmp.get(id) || [];
      list.push(req);
      availedByEmp.set(id, list);
    });
    const earnedByEmp = new Map();
    (overtime || []).forEach((ot) => {
      const id = ot.employee?.id || ot.employee;
      if (!id) return;
      const hrs = overtimeHours(ot);
      earnedByEmp.set(id, (earnedByEmp.get(id) || 0) + hrs);
    });

    const statusFilter = filters.availmentStatus;
    const rows = [];
    for (const emp of employees) {
      const remaining = creditByEmp.get(emp.id) ?? 0;
      const availedList = availedByEmp.get(emp.id) || [];
      const availedTotal = availedList.reduce(
        (sum, r) => sum + (Number(r.creditDeduction) || 0),
        0,
      );
      const earned = earnedByEmp.get(emp.id) || 0;
      const hasAvailed = availedList.length > 0;
      if (statusFilter === "availed" && !hasAvailed) continue;
      if (statusFilter === "non_availed" && hasAvailed) continue;

      if (hasAvailed) {
        availedList.forEach((req) => {
          const ranges = flattenDateRanges(req.dateRange);
          rows.push({
            employeeName: empName(emp),
            employeeNo: emp.agency || "",
            office: officeLabel(emp),
            cocEarned: earned,
            cocAvailed: Number(req.creditDeduction) || 0,
            cocRemaining: remaining,
            dateAvailed: ranges.map((r) => `${r.from} – ${r.to}`).join("; "),
            status: req.stats || (hasAvailed ? "Availed" : "Non-Availed"),
          });
        });
      } else {
        rows.push({
          employeeName: empName(emp),
          employeeNo: emp.agency || "",
          office: officeLabel(emp),
          cocEarned: earned,
          cocAvailed: availedTotal,
          cocRemaining: remaining,
          dateAvailed: "",
          status: remaining > 0 ? "Non-Availed" : "No Balance",
        });
      }
    }
    return rows;
  }

  // ── 2. Custom Timekeeping ────────────────────────────────────────────────
  async fetchCustomTimekeepingReport(filters = {}) {
    const { monthIndex, year } = monthYearFromFilters(filters);
    if (monthIndex < 0 || !year) {
      throw new Error("Month and Year are required.");
    }
    const employees = await this.resolveEmployeesForAttendance(filters);
    return this.fetchAttendanceForEmployees(employees, monthIndex, year);
  }

  // ── 3. Daily In/Out ──────────────────────────────────────────────────────
  async fetchDailyInOutReport(filters = {}) {
    const { monthIndex, year } = monthYearFromFilters(filters);
    if (monthIndex < 0 || !year) {
      throw new Error("Month and Year are required.");
    }
    const employees = await this.resolveEmployeesForAttendance(filters);
    return this.fetchAttendanceForEmployees(employees, monthIndex, year);
  }

  // ── 4. DTR Corrections (biometric_logs — same as TimeFormPage / Time Management)
  async fetchDtrCorrectionsReport(filters = {}) {
    const { start, end } = dateRangeFromFilters(filters);
    if (!start || !end) {
      throw new Error("Date Range is required.");
    }

    // Same collection TimeFormPage upserts: biometric_logs with origin: "correction".
    const employees = await this.resolveEmployees(filters);
    if (!employees.length) return [];

    const pinToEmp = new Map();
    employees.forEach((emp) => {
      if (emp?.biometricId == null || emp.biometricId === "") return;
      pinToEmp.set(String(emp.biometricId), emp);
    });
    if (pinToEmp.size === 0) return [];

    const pins = [...pinToEmp.keys()];
    const timeWhere = {
      origin: "correction",
      timestamp: {
        $gte: `${start}T00:00:00.000Z`,
        $lte: `${end}T23:59:59.999Z`,
      },
    };
    // Exact pin when one employee; otherwise date + origin where, filter pin client-side.
    const where =
      pins.length === 1 ? { ...timeWhere, pin: pins[0] } : { ...timeWhere };

    let logs;
    try {
      logs =
        (await this.find.execute("biometric_logs", {
          where,
          keys: [
            "pin",
            "timestamp",
            "status",
            "origin",
            "createdAt",
            "updatedAt",
            "id",
          ],
          sort: { timestamp: -1 },
          limit: ONE_SHOT_LIMIT,
        })) || [];
    } catch (e) {
      throwIfRateLimited(e);
      throw e;
    }

    // F1–F4 mapping matches TimeFormPage statusToTimeField / statusToMode.
    const punchTypeOf = {
      0: "Time In (AM)",
      1: "Time Out (PM)",
      2: "AM Out",
      3: "PM In",
    };
    const modeOf = { 0: "F1", 1: "F2", 2: "F3", 3: "F4" };

    const formatStamp = (value) => {
      if (!value) return "";
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value).slice(0, 19);
      return d.toISOString().slice(0, 19).replace("T", " ");
    };

    return logs
      .filter((log) => pinToEmp.has(String(log.pin)))
      .map((log) => {
        const emp = pinToEmp.get(String(log.pin));
        const status = Number(log.status);
        const ts = log.timestamp ? new Date(log.timestamp) : null;
        const validTs = ts && !Number.isNaN(ts.getTime());
        return {
          employeeName: empName(emp),
          employeeNo: emp?.agency || emp?.biometricId || "",
          date: validTs ? ts.toISOString().slice(0, 10) : "",
          punchType: punchTypeOf[status] || String(log.status ?? ""),
          mode: modeOf[status] || "",
          time: validTs ? ts.toISOString().slice(11, 19) : "",
          updated: formatStamp(log.updatedAt),
          created: formatStamp(log.createdAt),
        };
      })
      .sort((a, b) => {
        const dateCmp = String(b.date).localeCompare(String(a.date));
        if (dateCmp !== 0) return dateCmp;
        return String(a.employeeName).localeCompare(String(b.employeeName));
      });
  }

  // ── 5. List of AWOL Employees ────────────────────────────────────────────
  async fetchAwolReport(filters = {}) {
    const { monthIndex, year } = monthYearFromFilters(filters);
    if (monthIndex < 0 || !year) {
      throw new Error("Month and Year are required.");
    }
    const employees = await this.resolveEmployeesForAttendance(filters);
    const logs = await this.fetchAttendanceForEmployees(
      employees,
      monthIndex,
      year,
    );

    // BiometricDTR already skips approved leave / OB / holiday / rest day
    // before tagging ABSENT — reuse that as the AWOL source of truth.
    const byEmp = new Map();
    logs.forEach((log) => {
      const id = log.employee?.id;
      if (!id) return;
      if (!byEmp.has(id)) {
        byEmp.set(id, {
          employeeName: log.employeeName,
          employeeNo: log.employeeNo,
          awolDates: [],
          presentDates: [],
        });
      }
      const agg = byEmp.get(id);
      if (hasStat(log, "ABSENT")) {
        if (log.date) agg.awolDates.push(log.date);
        return;
      }
      if (hasPresentPunch(log) && log.date) {
        agg.presentDates.push(log.date);
      }
    });

    return Array.from(byEmp.values())
      .filter((r) => r.awolDates.length > 0)
      .map((r) => {
        const awolDates = [...new Set(r.awolDates)].sort();
        const presentDates = [...new Set(r.presentDates)].sort();
        return {
          employeeName: r.employeeName,
          employeeNo: r.employeeNo,
          awolDates: awolDates.join(", "),
          awolDays: awolDates.length,
          consecutiveAwolDays: maxConsecutiveDayStreak(awolDates),
          lastAttendance: presentDates[presentDates.length - 1] || "",
          status: "AWOL",
        };
      })
      .sort((a, b) => b.consecutiveAwolDays - a.consecutiveAwolDays);
  }

  // ── 6. Official Business ─────────────────────────────────────────────────
  async fetchOfficialBusinessReport(filters = {}) {
    // Match Finance → OB Management filters (no org hierarchy).
    const where = {};
    const employee = getSelectedEmployee(filters);
    if (employee?.id) where["employee.id"] = employee.id;
    if (filters.status) where.status = filters.status;

    const requested = filters.dateRequested || {};
    const requestedStart =
      pickDate(requested.start) ||
      pickDate(requested.createdAt) ||
      pickDate(requested.$gte);
    const requestedEnd =
      pickDate(requested.end) ||
      pickDate(requested.dateApproved) ||
      (requested.createdAt?.$lte ? pickDate(requested.createdAt.$lte) : null) ||
      pickDate(requested.$lte);
    if (requestedStart && requestedEnd) {
      // Same shape as OBManagementPage: bound createdAt to the selected range.
      let endDay = requestedEnd;
      try {
        const update = new Date(requestedEnd);
        update.setDate(update.getDate() + 1);
        endDay = update.toISOString().split("T")[0];
      } catch (_) {
        /* keep requestedEnd */
      }
      where.createdAt = { $gte: requestedStart, $lte: endDay };
    }

    const obDates = filters.obDates || {};
    if (obDates.obDates) {
      where.obDates = obDates.obDates;
    } else {
      const obStart = pickDate(obDates.start) || pickDate(obDates.$gte);
      const obEnd = pickDate(obDates.end) || pickDate(obDates.$lte);
      if (obStart && obEnd) {
        where.obDates = { $elemMatch: { $gte: obStart, $lte: obEnd } };
      }
    }

    // Prefer finance-approved collection; fall back to supervisor queue (not on 429)
    const obQuery = {
      where,
      include: ["employee"],
      keys: [
        "employee",
        "destination",
        "purpose",
        "selectedDate",
        "obDates",
        "status",
        "dateApproved",
        "createdAt",
      ],
      sort: { createdAt: -1 },
      limit: 1000,
    };

    let records;
    try {
      records = await this.find.execute("ob_management", obQuery);
    } catch (e) {
      throwIfRateLimited(e);
      records = null;
    }

    if (!records?.length) {
      try {
        records = await this.find.execute("ob_requests", obQuery);
      } catch (e) {
        throwIfRateLimited(e);
        throw e;
      }
    }

    return (records || []).map((rec) => {
      const ranges = flattenDateRanges(rec.selectedDate);
      const emp = rec.employee || {};
      return {
        employeeName: empName(emp),
        office: officeLabel(emp),
        obDate:
          (rec.obDates || []).join(", ") ||
          ranges.map((r) => `${r.from}–${r.to}`).join("; "),
        dateFrom: ranges[0]?.from || "",
        dateTo: ranges[ranges.length - 1]?.to || ranges[0]?.to || "",
        destination: rec.destination || "",
        purpose: rec.purpose || "",
        status: rec.status || "",
        dateApproved: rec.dateApproved
          ? String(rec.dateApproved).slice(0, 10)
          : "",
      };
    });
  }

  // ── 6. Perfect Attendance ────────────────────────────────────────────────
  async fetchPerfectAttendanceReport(filters = {}) {
    const { monthIndex, year } = monthYearFromFilters(filters);
    if (monthIndex < 0 || !year) {
      throw new Error("Month and Year are required.");
    }
    const employees = await this.resolveEmployeesForAttendance(filters);
    const logs = await this.fetchAttendanceForEmployees(
      employees,
      monthIndex,
      year,
    );

    const byEmp = new Map();
    logs.forEach((log) => {
      const id = log.employee?.id;
      if (!id) return;
      if (!byEmp.has(id)) {
        byEmp.set(id, {
          employee: log.employee,
          employeeName: log.employeeName,
          employeeNo: log.employeeNo,
          office: log.office,
          daysPresent: 0,
          lateCount: 0,
          undertimeCount: 0,
          absenceCount: 0,
          obDays: 0,
        });
      }
      const agg = byEmp.get(id);
      const stats = statsList(log);
      const isOb = stats.some((s) =>
        String(s).toLowerCase().includes("official business"),
      );
      if (isOb) agg.obDays += 1;
      if (hasStat(log, "ABSENT")) {
        agg.absenceCount += 1;
        return;
      }
      if (hasStat(log, "LATE")) agg.lateCount += 1;
      if (hasStat(log, "UNDERTIME")) agg.undertimeCount += 1;
      // Working day with punches or OB counts as present for perfect attendance
      if (
        (log.timeIn && log.timeIn !== "--:--") ||
        (log.timePmIn && log.timePmIn !== "--:--") ||
        isOb
      ) {
        agg.daysPresent += 1;
      }
    });

    return Array.from(byEmp.values())
      .filter(
        (r) =>
          r.absenceCount === 0 && r.lateCount === 0 && r.undertimeCount === 0,
      )
      .map((r) => ({
        ...r,
        status: "Perfect Attendance",
      }));
  }

  // ── 7. Tardiness Frequency ───────────────────────────────────────────────
  async fetchTardinessFrequencyReport(filters = {}) {
    const { monthIndex, year } = monthYearFromFilters(filters);
    if (monthIndex < 0 || !year) {
      throw new Error("Month and Year are required.");
    }
    const employees = await this.resolveEmployeesForAttendance(filters);
    const logs = await this.fetchAttendanceForEmployees(
      employees,
      monthIndex,
      year,
    );

    const byEmp = new Map();
    logs.forEach((log) => {
      // Count only days with real late minutes. timeRecStats can still carry
      // a LATE tag when basicLateMinutes is empty (e.g. print/absent/carry-over),
      // which incorrectly adds dates like the 27th with 0 minutes.
      const lateMinutes = Number(log.basicLateMinutes) || 0;
      if (lateMinutes <= 0) return;

      const id = log.employee?.id;
      if (!id) return;
      if (!byEmp.has(id)) {
        byEmp.set(id, {
          employeeName: log.employeeName,
          employeeNo: log.employeeNo,
          lateDates: [],
          totalLateMinutes: 0,
        });
      }
      const agg = byEmp.get(id);
      const lateDate =
        log.date ||
        (log.stampDate
          ? new Date(log.stampDate).toISOString().slice(0, 10)
          : null);
      if (lateDate) agg.lateDates.push(lateDate);
      agg.totalLateMinutes += lateMinutes;
    });

    return Array.from(byEmp.values())
      .filter((r) => r.totalLateMinutes > 0)
      .map((r) => {
        const lateDates = [...new Set(r.lateDates)].sort();
        return {
          employeeName: r.employeeName,
          employeeNo: r.employeeNo,
          lateDates: lateDates.join(", "),
          totalLateMinutes: r.totalLateMinutes,
        };
      })
      .sort((a, b) => b.totalLateMinutes - a.totalLateMinutes);
  }

  // ── 8. Leave Credits (leave_cred) ────────────────────────────────────────
  async fetchLeaveCreditsReport(filters = {}) {
    // Same leave_cred shape used by employee Leave Credits / Leave Request:
    // { empID: employee.id, type: "Sick Leave"|..., current: "12.000" }
    // Full org match (paged) — light query, not BiometricDTR.
    const employees = await this.resolveEmployees(filters);
    if (!employees.length) return [];

    const empById = new Map(employees.map((e) => [e.id, e]));
    const empIds = employees.map((e) => e.id).filter(Boolean);
    // Some older rows may store email in empID — include both.
    const empEmails = employees.map((e) => e.email).filter(Boolean);
    const empKeys = [...new Set([...empIds, ...empEmails])];

    let credits;
    try {
      // One find with where (exact empID when single; else type-free scan + client filter).
      // Avoids large `$in` and batch loops.
      credits = await findOnceByWhere(this.find, {
        collection: "leave_cred",
        idField: "empID",
        ids: empKeys,
        keys: ["empID", "email", "type", "current", "createdAt", "updatedAt"],
        sort: { type: 1 },
      });
    } catch (e) {
      throwIfRateLimited(e);
      throw e;
    }

    const rows = (credits || [])
      .map((cred) => {
        const emp =
          empById.get(cred.empID) ||
          employees.find((e) => e.email && e.email === cred.empID) ||
          null;
        return {
          employeeName: empName(emp) || cred.email || cred.empID || "",
          employeeNo: emp?.agency || emp?.biometricId || "",
          leaveType: cred.type || "",
          currentCredits: cred.current ?? "",
        };
      })
      .filter((r) => r.leaveType);

    return rows.sort((a, b) => {
      const nameCmp = String(a.employeeName).localeCompare(
        String(b.employeeName),
      );
      if (nameCmp !== 0) return nameCmp;
      return String(a.leaveType).localeCompare(String(b.leaveType));
    });
  }

  // ── 9. Personal Travel Abroad/Local (Travel Authority) ───────────────────
  async fetchTravelAuthorityReport(filters = {}) {
    const { start, end } = dateRangeFromFilters(filters);
    const employee = getSelectedEmployee(filters);
    const hierarchy = normalizeHierarchy(filters.hierarchy) || {};
    const hasHierarchy = hasHierarchyScope(hierarchy);

    // Optional org scope — resolve employee ids, then filter leave rows client-side
    // (avoids large unsupported $in on leave_request).
    let empIdSet = null;
    if (employee?.id) {
      empIdSet = new Set([employee.id]);
    } else if (hasHierarchy) {
      const employees = await this.resolveEmployees(filters);
      empIdSet = new Set(employees.map((e) => e.id).filter(Boolean));
      if (empIdSet.size === 0) return [];
    }

    const dateWhere =
      start && end
        ? {
            dateRange: {
              $elemMatch: {
                $elemMatch: { 0: { $lte: end }, 1: { $gte: start } },
              },
            },
          }
        : {};

    const baseWhere = {
      stats: "Approved",
      ...dateWhere,
      ...(employee?.id ? { "employee.id": employee.id } : {}),
    };

    const keys = [
      "employee",
      "selectedType",
      "leaveType",
      "abroad",
      "philippines",
      "abroadspecify",
      "dateRange",
      "stats",
      "createdAt",
      "updatedAt",
    ];

    // Two exact where clauses (no large $or): abroad OR philippines.
    let abroadRows = [];
    let localRows = [];
    try {
      abroadRows =
        (await this.find.execute("leave_request", {
          where: { ...baseWhere, abroad: true },
          include: ["employee"],
          keys,
          sort: { createdAt: -1 },
          limit: ONE_SHOT_LIMIT,
        })) || [];
      localRows =
        (await this.find.execute("leave_request", {
          where: { ...baseWhere, philippines: true },
          include: ["employee"],
          keys,
          sort: { createdAt: -1 },
          limit: ONE_SHOT_LIMIT,
        })) || [];
    } catch (e) {
      throwIfRateLimited(e);
      throw e;
    }

    const byId = new Map();
    [...abroadRows, ...localRows].forEach((row) => {
      if (!row) return;
      const isTravel = !!row.abroad || !!row.philippines;
      if (!isTravel) return;
      const empId = row.employee?.id;
      if (empIdSet && empId && !empIdSet.has(empId)) return;
      const key = row.id || `${empId}-${row.selectedType}-${row.createdAt}`;
      if (!byId.has(key)) byId.set(key, row);
    });

    const travelTypeOf = (row) => {
      const parts = [];
      if (row.abroad) parts.push("Abroad");
      if (row.philippines) parts.push("Local (Philippines)");
      return parts.join(" / ") || "";
    };

    return Array.from(byId.values())
      .map((req) => {
        const emp = req.employee || {};
        const ranges = flattenDateRanges(req.dateRange);
        return {
          employeeName: empName(emp),
          employeeNo: emp.agency || emp.biometricId || "",
          leaveType: req.selectedType || req.leaveType?.name || "",
          travelType: travelTypeOf(req),
          destination: req.abroadspecify || "",
          dateFrom: ranges[0]?.from || "",
          dateTo: ranges[ranges.length - 1]?.to || ranges[0]?.to || "",
          status: req.stats || "Approved",
        };
      })
      .sort((a, b) => {
        const nameCmp = String(a.employeeName).localeCompare(
          String(b.employeeName),
        );
        if (nameCmp !== 0) return nameCmp;
        return String(a.dateFrom).localeCompare(String(b.dateFrom));
      });
  }

  async run(serviceName, filters) {
    this.beginRun();
    const fn = this[serviceName];
    if (typeof fn !== "function") {
      throw new Error(`Unknown report service: ${serviceName}`);
    }
    try {
      return await fn.call(this, filters);
    } finally {
      // keep cancelled flag if cancelRun() was called mid-flight
    }
  }
}
